import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useState, useEffect, useRef, useCallback } from "react";
import {
  CreditCard,
  Bitcoin,
  DollarSign,
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Shield,
  ChevronDown,
  RefreshCw,
  ExternalLink,
  Clock,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { ApiError, api, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { loadStripe, type Stripe, type StripeElements } from "@stripe/stripe-js";

/** These two calls keep raw fetch (they branch on non-JSON shapes); reuse the token
 *  accessor rather than reading localStorage again. */
const authHeader = (): Record<string, string> => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const Route = createFileRoute("/_app/recharge")({
  component: RechargePage,
  // NOWPayments redirects back with ?crypto_status=success|cancelled&NP_id=<payment_id>
  // Without validateSearch TanStack Router throws a validation error and shows a blank page.
  validateSearch: (search: Record<string, unknown>) => ({
    crypto_status: (search.crypto_status as string) || undefined,
    NP_id: (search.NP_id as string) || undefined,
  }),
});

// ─── Types ─────────────────────────────────────────────────────────────────────

type PaymentStep =
  "select-method" | "card-form" | "crypto-form" | "crypto-pending" | "success" | "cancelled";

// ─── Currency display helpers ──────────────────────────────────────────────────

const CURRENCY_META: Record<string, { label: string; icon: string; color: string }> = {
  btc: { label: "Bitcoin", icon: "₿", color: "#F7931A" },
  eth: { label: "Ethereum", icon: "Ξ", color: "#627EEA" },
  usdttrc20: { label: "USDT (TRC-20)", icon: "₮", color: "#26A17B" },
  usdterc20: { label: "USDT (ERC-20)", icon: "₮", color: "#26A17B" },
  ltc: { label: "Litecoin", icon: "Ł", color: "#345D9D" },
  xrp: { label: "Ripple (XRP)", icon: "✕", color: "#346AA9" },
  bnbbsc: { label: "BNB (BSC)", icon: "⬡", color: "#F3BA2F" },
  sol: { label: "Solana", icon: "◎", color: "#9945FF" },
  trx: { label: "TRON (TRX)", icon: "T", color: "#EF0027" },
  doge: { label: "Dogecoin", icon: "D", color: "#C2A633" },
};

function getCurrencyLabel(code: string) {
  return CURRENCY_META[code]?.label ?? code.toUpperCase();
}
function getCurrencyIcon(code: string) {
  return CURRENCY_META[code]?.icon ?? "₿";
}
function getCurrencyColor(code: string) {
  return CURRENCY_META[code]?.color ?? "#a78bfa";
}

// ─── Stripe Card Form Component ─────────────────────────────────────────────────

function StripeCardForm({
  amount,
  onSuccess,
  onBack,
}: {
  amount: number;
  onSuccess: () => void;
  onBack: () => void;
}) {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [elements, setElements] = useState<StripeElements | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardError, setCardError] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string>("");
  const [publishableKey, setPublishableKey] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  const { t } = useI18n();

  useEffect(() => {
    const setup = async () => {
      setIsLoading(true);
      try {
        const { publishable_key } = await api.get<{ publishable_key: string }>(
          "/api/stripe/config",
        );

        if (!publishable_key || publishable_key.includes("REPLACE_WITH")) {
          throw new Error(t("recharge.stripe_not_configured"));
        }
        setPublishableKey(publishable_key);

        const { client_secret } = await api.post<{ client_secret: string }>(
          "/api/stripe/create-payment-intent",
          { amount },
        );
        setClientSecret(client_secret);

        const stripeInstance = await loadStripe(publishable_key);
        setStripe(stripeInstance);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : t("common.error");
        toast.error(message);
        onBack();
      } finally {
        setIsLoading(false);
      }
    };
    setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  useEffect(() => {
    if (!stripe || !clientSecret || !containerRef.current || mountedRef.current) return;
    mountedRef.current = true;

    const stripeElements = stripe.elements({
      clientSecret,
      appearance: {
        theme: "night",
        variables: {
          colorPrimary: "#a78bfa",
          colorBackground: "#1e1b2e",
          colorText: "#e5e7eb",
          colorDanger: "#ef4444",
          fontFamily: "'Inter', system-ui, sans-serif",
          borderRadius: "12px",
          spacingUnit: "4px",
        },
      },
    });

    const paymentElement = stripeElements.create("payment", { layout: "tabs" });
    paymentElement.on("change", (event) => {
      setCardError(event.error ? event.error.message : "");
    });
    paymentElement.mount(containerRef.current);
    setElements(stripeElements);
  }, [stripe, clientSecret]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;

    setIsSubmitting(true);
    setCardError("");

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
      confirmParams: { return_url: `${window.location.origin}/recharge` },
    });

    if (error) {
      setCardError(error.message || t("recharge.payment_failed_retry"));
      setIsSubmitting(false);
    } else {
      setTimeout(() => {
        window.dispatchEvent(new Event("balance_updated"));
        onSuccess();
      }, 2000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4">
        <div className="relative">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-primary animate-spin" />
          </div>
          <div className="absolute inset-0 rounded-full bg-primary/5 animate-ping" />
        </div>
        <p className="text-muted-foreground text-sm">{t("recharge.loading_form")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg px-4 py-2 border border-border/50">
        <Shield className="h-3.5 w-3.5 text-green-400 shrink-0" />
        <span>{t("recharge.stripe_secure_note")}</span>
      </div>

      <div>
        <label className="text-sm font-medium mb-3 block text-foreground">
          {t("recharge.payment_details")}
        </label>
        <div
          ref={containerRef}
          className="rounded-xl overflow-hidden border border-primary/20 bg-[#1e1b2e] min-h-[200px] p-1"
          id="stripe-payment-element"
        />
      </div>

      {cardError && (
        <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 rounded-lg px-4 py-3 border border-destructive/20">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{cardError}</span>
        </div>
      )}

      <div className="bg-primary/5 rounded-xl p-4 border border-primary/10 flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
            {t("recharge.amount_to_charge")}
          </p>
          <p className="text-2xl font-bold text-foreground mt-1">${amount.toFixed(2)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{t("recharge.credits_to_add")}</p>
          <p className="text-xl font-semibold text-primary mt-1">${amount.toFixed(2)}</p>
        </div>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
          className="flex-1"
          id="stripe-back-btn"
        >
          {t("recharge.back")}
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !stripe || !elements}
          className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
          id="stripe-pay-btn"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("recharge.processing")}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              {t("recharge.pay_amount", { amount: `$${amount.toFixed(2)}` })}
            </span>
          )}
        </Button>
      </div>

      {publishableKey.startsWith("pk_test_") && (
        <p className="text-center text-xs text-muted-foreground">
          🧪 {t("recharge.test_mode", { card: "4242 4242 4242 4242" })}
        </p>
      )}
    </form>
  );
}

// ─── Crypto Payment Form ────────────────────────────────────────────────────────

function CryptoPaymentForm({
  amount,
  onSuccess,
  onBack,
  onPending,
}: {
  amount: number;
  onSuccess: () => void;
  onBack: () => void;
  onPending: (paymentId: string, invoiceUrl: string) => void;
}) {
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState("usdttrc20");
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { t } = useI18n();

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const data = await api.get<{ currencies?: string[] }>("/api/crypto/currencies");
        setCurrencies(data.currencies || []);
      } catch {
        setCurrencies(Object.keys(CURRENCY_META));
      } finally {
        setIsLoadingCurrencies(false);
      }
    };
    fetchCurrencies();
  }, []);

  const handleCreatePayment = async () => {
    setIsCreating(true);
    try {
      const res = await fetch("/api/crypto/create-payment", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ amount, currency: selectedCurrency }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || t("recharge.crypto_create_failed"));
      }

      const { payment_id, invoice_url } = await res.json();

      // Save payment_id and invoice_url so they survive the redirect back
      try {
        sessionStorage.setItem("np_pending_payment_id", payment_id);
        sessionStorage.setItem("np_pending_invoice_url", invoice_url);
      } catch {}

      // Go to pending screen first — then redirect the user to NOWPayments.
      // We do it this way so onPending is registered before navigation.
      onPending(payment_id, invoice_url);

      // Open NOWPayments in a new tab — keep the user on the pending screen
      setTimeout(() => {
        window.open(invoice_url, "_blank", "noopener,noreferrer");
      }, 300);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("recharge.crypto_create_failed");
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Security badge */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg px-4 py-2 border border-border/50">
        <Shield className="h-3.5 w-3.5 text-green-400 shrink-0" />
        <span>{t("recharge.crypto_secure_note")}</span>
      </div>

      {/* Currency selector */}
      <div>
        <label className="text-sm font-medium mb-3 block text-foreground">
          {t("recharge.select_crypto")}
        </label>
        {isLoadingCurrencies ? (
          <div className="flex items-center gap-2 py-3 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("recharge.loading_currencies")}
          </div>
        ) : (
          <div className="relative">
            <button
              type="button"
              id="crypto-currency-selector"
              onClick={() => setShowDropdown((p) => !p)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-primary/30 bg-background hover:border-primary/60 transition-colors text-left"
            >
              <div className="flex items-center gap-3">
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                  style={{ backgroundColor: getCurrencyColor(selectedCurrency) }}
                >
                  {getCurrencyIcon(selectedCurrency)}
                </div>
                <div>
                  <p className="font-semibold text-foreground">
                    {getCurrencyLabel(selectedCurrency)}
                  </p>
                  <p className="text-xs text-muted-foreground uppercase">{selectedCurrency}</p>
                </div>
              </div>
              <ChevronDown
                className={`h-4 w-4 text-muted-foreground transition-transform ${showDropdown ? "rotate-180" : ""}`}
              />
            </button>

            {showDropdown && (
              <div className="absolute z-50 w-full mt-2 rounded-xl border border-border bg-card shadow-xl max-h-60 overflow-y-auto">
                {currencies.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left ${
                      code === selectedCurrency ? "bg-primary/10" : ""
                    }`}
                    onClick={() => {
                      setSelectedCurrency(code);
                      setShowDropdown(false);
                    }}
                    id={`crypto-currency-${code}`}
                  >
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
                      style={{ backgroundColor: getCurrencyColor(code) }}
                    >
                      {getCurrencyIcon(code)}
                    </div>
                    <div>
                      <p className="font-medium text-foreground text-sm">
                        {getCurrencyLabel(code)}
                      </p>
                      <p className="text-xs text-muted-foreground uppercase">{code}</p>
                    </div>
                    {code === selectedCurrency && (
                      <CheckCircle className="h-4 w-4 text-primary ml-auto shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Amount summary */}
      <div className="bg-primary/5 rounded-xl p-4 border border-primary/10 flex items-center justify-between">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">
            {t("recharge.amount_to_pay")}
          </p>
          <p className="text-2xl font-bold text-foreground mt-1">${amount.toFixed(2)} USD</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">{t("recharge.credits_to_add")}</p>
          <p className="text-xl font-semibold text-primary mt-1">${amount.toFixed(2)}</p>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 text-sm text-blue-300 space-y-1">
        <p className="font-semibold text-blue-200">{t("recharge.how_it_works")}</p>
        <ol className="list-decimal list-inside space-y-0.5 text-xs text-blue-300/80">
          <li>{t("recharge.crypto_step1", { currency: getCurrencyLabel(selectedCurrency) })}</li>
          <li>{t("recharge.crypto_step2")}</li>
          <li>{t("recharge.crypto_step3")}</li>
        </ol>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={isCreating}
          className="flex-1"
          id="crypto-back-btn"
        >
          {t("recharge.back")}
        </Button>
        <Button
          type="button"
          onClick={handleCreatePayment}
          disabled={isCreating || isLoadingCurrencies}
          className="flex-1 font-semibold"
          style={{ backgroundColor: getCurrencyColor(selectedCurrency) }}
          id="crypto-pay-btn"
        >
          {isCreating ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("recharge.creating_invoice")}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              {t("recharge.pay_with", { currency: getCurrencyLabel(selectedCurrency) })}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Crypto Pending Screen (polling) ──────────────────────────────────────────

const POLL_STATUS_MAP: Record<string, { labelKey: string; color: string; icon: React.ReactNode }> =
  {
    waiting: {
      labelKey: "recharge.status_waiting",
      color: "text-yellow-400",
      icon: <Clock className="h-5 w-5" />,
    },
    confirming: {
      labelKey: "recharge.status_confirming",
      color: "text-blue-400",
      icon: <Loader2 className="h-5 w-5 animate-spin" />,
    },
    confirmed: {
      labelKey: "recharge.status_confirmed",
      color: "text-green-400",
      icon: <CheckCircle className="h-5 w-5" />,
    },
    sending: {
      labelKey: "recharge.status_sending",
      color: "text-blue-400",
      icon: <Loader2 className="h-5 w-5 animate-spin" />,
    },
    partially_paid: {
      labelKey: "recharge.status_partially_paid",
      color: "text-orange-400",
      icon: <AlertCircle className="h-5 w-5" />,
    },
    finished: {
      labelKey: "recharge.status_finished",
      color: "text-green-400",
      icon: <CheckCircle className="h-5 w-5" />,
    },
    failed: {
      labelKey: "recharge.status_failed",
      color: "text-red-400",
      icon: <XCircle className="h-5 w-5" />,
    },
    refunded: {
      labelKey: "recharge.status_refunded",
      color: "text-orange-400",
      icon: <RefreshCw className="h-5 w-5" />,
    },
    expired: {
      labelKey: "recharge.status_expired",
      color: "text-red-400",
      icon: <XCircle className="h-5 w-5" />,
    },
  };

function CryptoPendingScreen({
  paymentId,
  invoiceUrl,
  amount,
  onSuccess,
  onBack,
}: {
  paymentId: string;
  invoiceUrl: string;
  amount: number;
  onSuccess: () => void;
  onBack: () => void;
}) {
  const [liveStatus, setLiveStatus] = useState<string>("waiting");
  const [credited, setCredited] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useI18n();

  const poll = useCallback(async () => {
    try {
      const res = await fetch(`/api/crypto/payment-status/${paymentId}`, {
        headers: authHeader(),
      });
      if (res.ok) {
        const data = await res.json();
        setLiveStatus(data.live_status || data.db_status);
        setCredited(data.credited);

        if (data.credited || data.live_status === "finished") {
          window.dispatchEvent(new Event("balance_updated"));
          setTimeout(onSuccess, 1500);
          return; // Stop polling
        }

        if (["failed", "refunded", "expired"].includes(data.live_status)) {
          return; // Stop polling on terminal failure
        }
      }
    } catch {
      // ignore network errors during polling
    }
    setPollCount((c) => c + 1);
  }, [paymentId, onSuccess]);

  useEffect(() => {
    // Poll every 5 seconds for up to 20 minutes (240 polls)
    poll();
    pollRef.current = setInterval(() => {
      setPollCount((c) => {
        if (c >= 240) {
          if (pollRef.current) clearInterval(pollRef.current);
          return c;
        }
        return c;
      });
      poll();
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [poll]);

  // Also poll when poll count changes (only needed for initial call)
  useEffect(() => {
    if (pollCount > 0 && ["failed", "refunded", "expired"].includes(liveStatus)) {
      if (pollRef.current) clearInterval(pollRef.current);
    }
  }, [pollCount, liveStatus]);

  const statusMeta = POLL_STATUS_MAP[liveStatus] ?? POLL_STATUS_MAP["waiting"];
  const isTerminalFailure = ["failed", "refunded", "expired"].includes(liveStatus);

  return (
    <div className="flex flex-col items-center justify-center py-10 space-y-6 text-center">
      {/* Animated icon */}
      <div className="relative">
        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center">
          <div className={statusMeta.color}>{statusMeta.icon}</div>
        </div>
        {!isTerminalFailure && !credited && (
          <div className="absolute inset-0 rounded-full bg-primary/5 animate-ping" />
        )}
      </div>

      <div className="space-y-2">
        <h2 className="text-xl font-bold text-foreground">{t("recharge.crypto_pending_title")}</h2>
        <p className={`font-semibold ${statusMeta.color}`}>{t(statusMeta.labelKey)}</p>
        <p className="text-muted-foreground text-sm">
          {t("recharge.amount_label")}{" "}
          <span className="text-foreground font-medium">${amount.toFixed(2)}</span>
        </p>
        <p className="text-xs text-muted-foreground font-mono bg-muted/30 rounded-lg px-3 py-1 mt-1">
          ID: {paymentId}
        </p>
      </div>

      {/* Go to payment button — shown while waiting */}
      {!isTerminalFailure && !credited && invoiceUrl && (
        <div className="w-full space-y-3">
          <a
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            id="crypto-open-payment-btn"
            className="flex items-center justify-center gap-2 w-full rounded-xl py-3 px-4 font-semibold text-white transition-all"
            style={{ backgroundColor: "#26A17B" }}
          >
            <ExternalLink className="h-4 w-4" />
            {t("recharge.complete_on_nowpayments")}
          </a>
          <p className="text-xs text-muted-foreground">👆 {t("recharge.complete_hint")}</p>
        </div>
      )}

      {!isTerminalFailure && !credited && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/20 rounded-lg px-4 py-2">
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          {t("recharge.checking_status")}
        </div>
      )}

      {isTerminalFailure && (
        <div className="space-y-3 w-full">
          <p className="text-sm text-muted-foreground">{t("recharge.payment_incomplete")}</p>
          <Button
            onClick={onBack}
            variant="outline"
            className="w-full"
            id="crypto-pending-back-btn"
          >
            {t("recharge.try_again")}
          </Button>
        </div>
      )}

      {credited && (
        <p className="text-green-400 font-semibold animate-pulse">{t("recharge.redirecting")}</p>
      )}
    </div>
  );
}

// ─── Success Screen ─────────────────────────────────────────────────────────────

function PaymentSuccess({
  amount,
  method,
  onDone,
}: {
  amount: number;
  method: "card" | "crypto";
  onDone: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-6 text-center">
      <div className="relative">
        <div className="h-20 w-20 rounded-full bg-green-500/10 flex items-center justify-center">
          <CheckCircle className="h-10 w-10 text-green-500" />
        </div>
        <div className="absolute inset-0 rounded-full bg-green-500/5 animate-ping" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-foreground">{t("recharge.success_title")}</h2>
        <p className="text-muted-foreground mt-2">
          {t("recharge.success_added", { amount: `$${amount.toFixed(2)}` })}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {method === "crypto"
            ? t("recharge.success_crypto_note")
            : t("recharge.success_card_note")}
        </p>
      </div>
      <Button
        onClick={onDone}
        className="bg-primary hover:bg-primary/90"
        id="payment-success-done-btn"
      >
        {t("recharge.back_to_recharge")}
      </Button>
    </div>
  );
}

// ─── Cancelled Screen ───────────────────────────────────────────────────────────

function PaymentCancelled({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-6 text-center">
      <div className="h-20 w-20 rounded-full bg-muted/30 flex items-center justify-center">
        <XCircle className="h-10 w-10 text-muted-foreground" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-foreground">{t("recharge.cancelled_title")}</h2>
        <p className="text-muted-foreground mt-2 text-sm">{t("recharge.cancelled_desc")}</p>
      </div>
      <Button onClick={onDone} variant="outline" id="payment-cancelled-done-btn">
        {t("recharge.back_to_recharge")}
      </Button>
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────────

function RechargePage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [limits, setLimits] = useState({
    stripe_min: 5.0,
    crypto_min: 19.0,
    max: 1000.0,
    // Assume both are on until the server says otherwise, so a slow config fetch
    // does not flash an empty page at someone trying to pay.
    stripe_enabled: true,
    crypto_enabled: true,
  });
  const [amount, setAmount] = useState<string>("20");
  const [step, setStep] = useState<PaymentStep>("select-method");
  const [paymentMethod, setPaymentMethod] = useState<"card" | "crypto">("card");
  const [cryptoPaymentId, setCryptoPaymentId] = useState<string>("");
  const [cryptoInvoiceUrl, setCryptoInvoiceUrl] = useState<string>("");

  // Payment limits and per-method switches, from the admin panel.
  useEffect(() => {
    fetch("/api/config/payment-limits")
      .then((r) => r.json())
      .then((data) => {
        setLimits(data);
        // Default to the lowest minimum among the methods that are actually switched
        // on. Using the crypto minimum when crypto is disabled would prefill an amount
        // far above what a card payment needs.
        const mins = [
          data.stripe_enabled === false ? null : data.stripe_min,
          data.crypto_enabled === false ? null : data.crypto_min,
        ].filter((v): v is number => typeof v === "number");
        if (mins.length) setAmount(String(Math.ceil(Math.max(...mins))));
      })
      .catch(() => {}); // keep defaults on error
  }, []);

  // Check URL params on mount.
  // NOWPayments redirects back with ?crypto_status=success|cancelled&NP_id=<payment_id>
  // validateSearch above ensures TanStack Router doesn't throw on these params.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cryptoStatus = params.get("crypto_status");
    // NP_id is the payment id NOWPayments appends to the success_url
    const npId = params.get("NP_id") || params.get("np_id");

    // Also try sessionStorage (saved before opening the tab)
    let restoredPaymentId = "";
    let restoredInvoiceUrl = "";
    try {
      restoredPaymentId = sessionStorage.getItem("np_pending_payment_id") || "";
      restoredInvoiceUrl = sessionStorage.getItem("np_pending_invoice_url") || "";
    } catch {}
    if (npId) restoredPaymentId = npId; // prefer URL param if present

    if (cryptoStatus === "success") {
      if (restoredPaymentId) {
        // Go to pending screen so polling can confirm the payment before showing success
        setCryptoPaymentId(restoredPaymentId);
        setCryptoInvoiceUrl(restoredInvoiceUrl);
        setPaymentMethod("crypto");
        setStep("crypto-pending");
        try {
          sessionStorage.removeItem("np_pending_payment_id");
          sessionStorage.removeItem("np_pending_invoice_url");
        } catch {}
      } else {
        setStep("success");
        setPaymentMethod("crypto");
      }
      // Clean URL
      window.history.replaceState({}, "", window.location.pathname);
    } else if (cryptoStatus === "cancelled") {
      setStep("cancelled");
      try {
        sessionStorage.removeItem("np_pending_payment_id");
        sessionStorage.removeItem("np_pending_invoice_url");
      } catch {}
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const parsedAmount = parseFloat(amount) || 0;

  const validateAmount = (method: "card" | "crypto" = "card") => {
    if (!user) {
      toast.error(t("recharge.login_required"));
      return false;
    }
    const min = method === "crypto" ? limits.crypto_min : limits.stripe_min;
    if (parsedAmount < min) {
      toast.error(t("recharge.min_amount", { amount: `$${min.toFixed(2)}` }));
      return false;
    }
    if (parsedAmount > limits.max) {
      toast.error(t("recharge.max_amount", { amount: `$${limits.max.toFixed(2)}` }));
      return false;
    }
    return true;
  };

  const handleCardClick = () => {
    if (!validateAmount("card")) return;
    setPaymentMethod("card");
    setStep("card-form");
  };

  const handleCryptoClick = () => {
    if (!validateAmount("crypto")) return;
    setPaymentMethod("crypto");
    setStep("crypto-form");
  };

  // ── Select Method ────────────────────────────────────────────────────────────
  if (step === "select-method") {
    return (
      <div className="mx-auto max-w-4xl space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("recharge.title")}</h1>
          <p className="mt-1 text-muted-foreground">{t("recharge.subtitle")}</p>
        </div>

        {/* Amount selector */}
        <Card className="p-6 border-border bg-card">
          <label className="text-sm font-medium mb-2 block">{t("recharge.amount")}</label>
          <div className="flex items-center gap-2 max-w-sm mb-4">
            <div className="relative flex-1">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="number"
                min={limits.stripe_min}
                max={limits.max}
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-9"
                placeholder={t("recharge.amount_ph")}
                id="recharge-amount-input"
              />
            </div>
          </div>
          <div className="flex gap-2 mb-2 flex-wrap">
            {(() => {
              // From the minimums of the methods that are switched on: a disabled
              // ("coming soon") crypto minimum must not push every preset up.
              const mins = [
                limits.stripe_enabled ? limits.stripe_min : null,
                limits.crypto_enabled ? limits.crypto_min : null,
              ].filter((v): v is number => typeof v === "number");
              const base = Math.ceil(mins.length ? Math.max(...mins) : limits.stripe_min);
              // Generate 5 sensible preset amounts starting from the lowest minimum
              const steps = [base, base * 2, base * 5, base * 10, base * 20].filter(
                (v) => v <= limits.max,
              );
              return steps.map((val) => (
                <Button
                  key={val}
                  variant={amount === String(val) ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAmount(String(val))}
                  id={`recharge-preset-${val}`}
                >
                  ${val}
                </Button>
              ));
            })()}
          </div>
        </Card>

        {/* Payment methods */}
        <div>
          <h2 className="text-xl font-semibold mb-4">{t("recharge.select_method")}</h2>
          <div className="grid gap-6 md:grid-cols-2">
            {/* ── Credit/Debit Card (Stripe) ──────────────────────────────────
                Hidden outright when the admin switches the method off: greying it out
                invites a click, and the endpoint would answer 503 anyway. */}
            {limits.stripe_enabled && (
              <Card
                className={`p-8 border-border bg-card flex flex-col items-center justify-center text-center space-y-4 relative overflow-hidden group transition-all ${
                  parsedAmount < limits.stripe_min
                    ? "opacity-50 cursor-not-allowed grayscale"
                    : "hover:border-primary/60 hover:shadow-glow cursor-pointer"
                }`}
                onClick={parsedAmount < limits.stripe_min ? undefined : handleCardClick}
                id="payment-method-card"
              >
                <div className="absolute inset-0 rounded-xl bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center text-primary mb-2 group-hover:bg-primary/20 transition-colors">
                  <CreditCard className="h-8 w-8" />
                </div>
                <h2 className="text-2xl font-semibold">{t("recharge.card_title")}</h2>
                <p className="text-muted-foreground text-sm">
                  {t("recharge.card_desc")}
                  <br />
                  <span className="font-semibold text-xs mt-1 block">
                    {t("recharge.min_label", { amount: `$${limits.stripe_min.toFixed(2)}` })}
                  </span>
                </p>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60 mt-2">
                  <Lock className="h-3 w-3" />
                  <span>{t("recharge.powered_stripe")}</span>
                </div>
              </Card>
            )}

            {/* ── Cryptocurrency (NOWPayments) ──
                Unlike the card, kept on screen when switched off and labelled "coming
                soon" (owner's call): crypto is a method we intend to offer, and a card
                that silently vanishes reads as "they do not take crypto". It is inert —
                no click handler — and the endpoint answers 503 anyway. */}
            {(() => {
              const cryptoOff = !limits.crypto_enabled;
              const tooLow = parsedAmount < limits.crypto_min;
              const inert = cryptoOff || tooLow;
              return (
                <Card
                  className={`p-8 border-border bg-card flex flex-col items-center justify-center text-center space-y-4 relative overflow-hidden group transition-all ${
                    inert
                      ? "opacity-50 cursor-not-allowed grayscale"
                      : "hover:border-amber-500/60 hover:shadow-[0_0_30px_rgba(245,158,11,0.15)] cursor-pointer"
                  }`}
                  onClick={inert ? undefined : handleCryptoClick}
                  aria-disabled={inert}
                  id="payment-method-crypto"
                >
                  {cryptoOff && (
                    <span className="absolute right-3 top-3 rounded-full border border-amber-500/40 bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-500">
                      {t("recharge.coming_soon")}
                    </span>
                  )}
                  <div className="absolute inset-0 rounded-xl bg-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="h-16 w-16 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-400 mb-2 group-hover:bg-amber-500/20 transition-colors">
                    <Bitcoin className="h-8 w-8" />
                  </div>
                  <h2 className="text-2xl font-semibold">{t("recharge.crypto_title")}</h2>
                  <p className="text-muted-foreground text-sm">
                    {t("recharge.crypto_desc")}
                    {!cryptoOff && (
                      <>
                        <br />
                        <span className="font-semibold text-xs mt-1 block">
                          {t("recharge.min_label", { amount: `$${limits.crypto_min.toFixed(2)}` })}
                        </span>
                      </>
                    )}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60 mt-2">
                    <Shield className="h-3 w-3 text-amber-400/60" />
                    <span>{t("recharge.powered_nowpayments")}</span>
                  </div>
                </Card>
              );
            })()}

            {!limits.stripe_enabled && !limits.crypto_enabled && (
              <Card className="col-span-full border-border bg-card p-8 text-center">
                <p className="text-sm text-muted-foreground">{t("recharge.methods_unavailable")}</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Card Form ────────────────────────────────────────────────────────────────
  if (step === "card-form") {
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <CreditCard className="h-6 w-6 text-primary" />
            {t("recharge.secure_checkout")}
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            {t("recharge.recharging_card", { amount: `$${parsedAmount.toFixed(2)}` })}
          </p>
        </div>
        <Card className="p-6 border-border bg-card">
          <StripeCardForm
            amount={parsedAmount}
            onSuccess={() => setStep("success")}
            onBack={() => setStep("select-method")}
          />
        </Card>
      </div>
    );
  }

  // ── Crypto Form ──────────────────────────────────────────────────────────────
  if (step === "crypto-form") {
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bitcoin className="h-6 w-6 text-amber-400" />
            {t("recharge.crypto_checkout")}
          </h1>
          <p className="mt-1 text-muted-foreground text-sm">
            {t("recharge.recharging_crypto", { amount: `$${parsedAmount.toFixed(2)}` })}
          </p>
        </div>
        <Card className="p-6 border-border bg-card">
          <CryptoPaymentForm
            amount={parsedAmount}
            onSuccess={() => setStep("success")}
            onBack={() => setStep("select-method")}
            onPending={(pid, iurl) => {
              setCryptoPaymentId(pid);
              setCryptoInvoiceUrl(iurl);
              setStep("crypto-pending");
            }}
          />
        </Card>
      </div>
    );
  }

  // ── Crypto Pending ───────────────────────────────────────────────────────────
  if (step === "crypto-pending") {
    return (
      <div className="mx-auto max-w-lg">
        <Card className="p-8 border-border bg-card">
          <CryptoPendingScreen
            paymentId={cryptoPaymentId}
            invoiceUrl={cryptoInvoiceUrl}
            amount={parsedAmount}
            onSuccess={() => setStep("success")}
            onBack={() => setStep("crypto-form")}
          />
        </Card>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────────
  if (step === "success") {
    return (
      <div className="mx-auto max-w-lg">
        <Card className="p-8 border-border bg-card">
          <PaymentSuccess
            amount={parsedAmount}
            method={paymentMethod}
            onDone={() => {
              setStep("select-method");
              setAmount("10");
            }}
          />
        </Card>
      </div>
    );
  }

  // ── Cancelled ────────────────────────────────────────────────────────────────
  if (step === "cancelled") {
    return (
      <div className="mx-auto max-w-lg">
        <Card className="p-8 border-border bg-card">
          <PaymentCancelled
            onDone={() => {
              setStep("select-method");
            }}
          />
        </Card>
      </div>
    );
  }

  return null;
}
